/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import {
    StickyBottomHelper,
    Icon,
    SecurityHelper,
    Spinner,
    useFlags,
    Heading,
    Button,
} from 'sarsaparilla';
import { CONTINUE_SHOPPING_EVENT } from 'shared-ui/constants';
import { notifyEvent } from 'shared-ui/utils';
import gnoment from 'ui-cart/shared-components/utils/gnoment';

import { fetchCampground } from 'ui-camping/src/shared/actions/campground';
import { getVenueRecurringReservations } from 'ui-venue/src/utils';
import RefundBox from 'ui-camping/src/shared/components/orderDetailsComponents/RefundBox';
import { checkInFlightFacilityTimeReservationStartDate } from '../utils/date_utils';

import ReservationTotal from './ReservationTotal';
import FuturePaymentBox from './FuturePaymentBox';
import { formatMoney } from '../utils/cart_utils';
import * as constants from '../constants';
import AddDiscount from './AddDiscount';

function OrderSummary(props) {
    const {
        reservations,
        subtotal,
        tax,
        total,
        hasError,
        discount,
        finalizeNoFee,
        checkout,
        page,
        renderActionBar,
        simple,
        offerParkpassUpsell,
        parkpassFacilityID,
        receiptEmail,
        hasInteragencyPass,
        isLoadingUpsell,
        offerActivityPassReminder,
        activityPassFacilityID,
        isLoadingActivityPassReminder,
        campingReservationData,
    } = props;

    const dispatch = useDispatch();
    const { disableNpsParkpasses } = useFlags();

    const getContinueShoppingUrl = () => {
        if (SecurityHelper.isWidgetSession()) {
            return SecurityHelper.getWidgetHomeUrl();
        }
        return '/';
    };

    const handleContinueShopping = useCallback(() => {
        notifyEvent(CONTINUE_SHOPPING_EVENT);
        window.location.assign(getContinueShoppingUrl());
    }, []);

    const [campground, setCampground] = useState({});
    const firstCampingResFacility = get(
        reservations?.[0],
        'order_details.facility_id',
        ''
    );

    useEffect(() => {
        if (firstCampingResFacility !== '') {
            dispatch(
                fetchCampground({
                    id: firstCampingResFacility,
                })
            ).then((response) => {
                setCampground(response);
            });
        }
    }, [dispatch, firstCampingResFacility]);

    const startDate = get(campingReservationData, 'start_date', '');
    const reservationStart = gnoment.utc(startDate);
    const facilityTimeZone = get(campground, 'facility_time_zone', 'America/New_York');
    const isReservationInFlight = checkInFlightFacilityTimeReservationStartDate({
        reservationStart,
        facilityTimeZone,
    });

    const urlParams = new URLSearchParams(window.location.search);
    const isWidget = urlParams.get('widget') === '1';

    if (SecurityHelper.isMobileApp() && SecurityHelper.isMobileNativeCartEnabled()) {
        // Don't render order summary when using mobile native cart
        return null;
    }

    let discountHtml = null;
    if (parseFloat(discount) !== 0) {
        discountHtml = (
            <tr>
                <th scope="row" className="cart-label">
                    Discount
                </th>
                <td className="cart-value">{formatMoney(discount)}</td>
            </tr>
        );
    }

    let buttonHtml = (
        <button type="button" disabled className="rec-button-primary-large">
            Proceed to Payment
        </button>
    );
    if (parseFloat(total) === 0 && !hasError && reservations && reservations.length > 0) {
        buttonHtml = (
            <button
                type="button"
                className="rec-button-primary-large"
                onClick={() => finalizeNoFee()}
            >
                Proceed to Confirmation
            </button>
        );
    } else if (parseFloat(total) > 0 && !hasError) {
        let blacklist = disableNpsParkpasses;
        if (!disableNpsParkpasses) {
            blacklist = [];
        }

        if (isLoadingUpsell || isLoadingActivityPassReminder) {
            buttonHtml = <Spinner />;
        } else if (
            offerParkpassUpsell &&
            parkpassFacilityID !== '' &&
            !blacklist.includes(parkpassFacilityID)
        ) {
            buttonHtml = (
                <Button
                    size="lg"
                    to="/cart/parkpass"
                    state={{
                        parkpassFacilityID,
                        receiptEmail,
                        hasInteragencyPass,
                    }}
                    className="mb-1"
                    shouldFitContainer
                >
                    Proceed to Payment
                </Button>
            );
        } else if (offerActivityPassReminder && activityPassFacilityID !== '') {
            buttonHtml = (
                <Button
                    size="lg"
                    to="/cart/activitypass"
                    state={{
                        activityPassFacilityID,
                        receiptEmail,
                    }}
                    className=" mb-1"
                    shouldFitContainer
                >
                    Proceed to Payment
                </Button>
            );
        } else {
            buttonHtml = (
                <Button size="lg" shouldFitContainer onClick={() => checkout()}>
                    Proceed to Payment
                </Button>
            );
        }
    }

    if (renderActionBar) {
        const singleReservationClassName =
            reservations.length === 1 ? 'single-reservation' : '';
        return (
            <StickyBottomHelper scrollingContainerId="page-body">
                <div className={`cart-page-actions ${singleReservationClassName}`}>
                    <div className="cart-action-details display-sm-block">
                        <div className="cart-subtotal">
                            Subtotal: {formatMoney(subtotal)} + tax
                        </div>
                        <div className="cart-total">Total Cost: {formatMoney(total)}</div>
                    </div>
                    {reservations &&
                    (page === constants.CHECKOUT_PAGE ||
                        page === constants.SELECT_PAYMENT) ? null : (
                        <div className="cart-action-continue">
                            <button
                                type="button"
                                className="rec-button-link rec-icon-left display-sm-none"
                                onClick={handleContinueShopping}
                            >
                                <Icon iconName="arrow-back" />
                                Continue Shopping
                            </button>
                        </div>
                    )}
                    {reservations &&
                    (page === constants.CHECKOUT_PAGE ||
                        page === constants.SELECT_PAYMENT) ? (
                        <div className="cart-action-button">
                            <Button
                                appearance="tertiary"
                                size="lg"
                                shouldFitContainer
                                to="/cart"
                            >
                                Return to Cart
                            </Button>
                        </div>
                    ) : (
                        <div className="cart-action-button">{buttonHtml}</div>
                    )}
                </div>
            </StickyBottomHelper>
        );
    }

    let reservationsData = reservations;
    const hasRecurringVenueReservations = reservations?.find(
        (item) =>
            (item?.inventory_type === constants.INVENTORY_VENUE_RESERVATIONS ||
                item?.inventory_type === constants.INVENTORY_VENUE_APPLICATION) &&
            item.order_details?.recurrence_id
    );

    if (hasRecurringVenueReservations) {
        reservationsData = getVenueRecurringReservations(reservations);
    }

    if (simple) {
        return (
            <div className="cart-order-summary">
                <div className="cart-order-summary-body">
                    {reservationsData ? (
                        <div className="cart-order-summary-block">
                            {reservationsData.map((reservation, index) => {
                                const key = `reservationTotal-${index}`;
                                return (
                                    <ReservationTotal
                                        key={key}
                                        reservation={reservation}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        ''
                    )}
                </div>
                <div className="cart-order-summary-total">
                    <table>
                        <tr>
                            <th scope="row" className="cart-label">
                                Total Cost
                            </th>
                            <td className="cart-value">{formatMoney(total)}</td>
                        </tr>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className="cart-order-summary">
            <div className="cart-order-summary-title">
                <Heading headingLevel={2} appearance="h5">
                    Order Summary
                </Heading>
            </div>
            <div className="cart-order-summary-body">
                {reservationsData ? (
                    <div className="cart-order-summary-block">
                        {reservationsData.map((reservation, index) => {
                            const key = `reservationTotal-${index}`;
                            return (
                                <ReservationTotal key={key} reservation={reservation} />
                            );
                        })}
                    </div>
                ) : (
                    ''
                )}
                <div className="cart-order-summary-block subtotal">
                    <table>
                        <tr>
                            <th scope="row" className="cart-label">
                                Subtotal
                            </th>
                            <td className="cart-value">{formatMoney(subtotal)}</td>
                        </tr>
                        {discountHtml}
                        <tr>
                            <th scope="row" className="cart-label">
                                Tax
                            </th>
                            <td className="cart-value">{formatMoney(tax)}</td>
                        </tr>
                    </table>
                </div>
            </div>
            <div className="cart-order-summary-total">
                <table>
                    <tr>
                        <th scope="row" className="cart-label">
                            Total Cost
                        </th>
                        <td className="cart-value">{formatMoney(total)}</td>
                    </tr>
                </table>
            </div>
            {reservations.length > 0 && !isReservationInFlight ? (
                <FuturePaymentBox reservations={reservations} />
            ) : null}
            {reservations.length === 1 && !isReservationInFlight ? (
                <RefundBox
                    type="alert"
                    reservations={campingReservationData}
                    campground={campground}
                />
            ) : null}
            {reservations &&
            (page === constants.CHECKOUT_PAGE || page === constants.SELECT_PAYMENT) ? (
                <div className="cart-order-summary-actions">
                    <Button appearance="tertiary" size="lg" shouldFitContainer to="/cart">
                        Return to Cart
                    </Button>
                </div>
            ) : (
                <div className="cart-order-summary-actions">
                    <AddDiscount hasDivider />
                    {buttonHtml}
                    {!isWidget && (
                        <Button
                            appearance="tertiary"
                            size="lg"
                            shouldFitContainer
                            onClick={handleContinueShopping}
                        >
                            Continue Shopping
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

OrderSummary.propTypes = {
    reservations: PropTypes.array,
    subtotal: PropTypes.string.isRequired,
    tax: PropTypes.string.isRequired,
    total: PropTypes.string.isRequired,
    hasError: PropTypes.bool,
    discount: PropTypes.string,
    finalizeNoFee: PropTypes.func,
    checkout: PropTypes.func,
    page: PropTypes.string,
    renderActionBar: PropTypes.bool,
    simple: PropTypes.bool,
    offerParkpassUpsell: PropTypes.bool,
    parkpassFacilityID: PropTypes.string,
    receiptEmail: PropTypes.string,
    hasInteragencyPass: PropTypes.bool,
    isLoadingUpsell: PropTypes.bool,
};

OrderSummary.defaultProps = {
    subtotal: '0',
    tax: '0',
    total: '0',
    renderActionBar: false,
    simple: false,
    offerParkpassUpsell: false,
    parkpassFacilityID: '',
    receiptEmail: '',
    hasInteragencyPass: false,
    isLoadingUpsell: false,
};

export default OrderSummary;

// cSpell:ignore activitypass, Parkpass, Parkpasses, upsell
